import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {ValidationStateInfo} from 'constants/SampleConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  StorableValidationStatusQuery,
  StorableValidationStatusQueryVariables,
} from 'inventory/location-validation/__generated/StorableValidationStatusQuery';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {SchemaValidationLongDisplayTextByStatus} from 'schemas/SchemaValidationConstants';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export const STORABLE_VALIDATION_STATUS_QUERY = gql`
  query StorableValidationStatusQuery($storableId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    storable(id: $storableId) {
      ... on StorableInterface {
        id
        validationState {
          id
          status
          validationErrorRecords {
            id
            errorLabel
            message
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface StorableValidationStatusIconProps {
  storable: {id: string} & ValidationStateInfo;
  className?: string;
}

interface QueryingStorableValidationStatusIconProps {
  storableId: string;
  className?: string;
}

export default function StorableValidationStatusIcon(
  props: StorableValidationStatusIconProps
): JSX.Element | null {
  const tooltipContent = useMemo(() => {
    const errorRecords = props.storable.validationState?.validationErrorRecords ?? [];
    const errorDetails =
      errorRecords.length === 1 ? (
        <span>{errorRecords[0].message}</span>
      ) : (
        errorRecords.map((error, idx) => <li key={idx}>{error.message}</li>)
      );
    return errorRecords.length > 0 ? (
      <div className={css(styles.tooltipText)}>
        {SchemaValidationLongDisplayTextByStatus.FAILED}: {errorDetails}
      </div>
    ) : (
      <div className={css(styles.tooltipText)}>{SchemaValidationLongDisplayTextByStatus.FAILED}</div>
    );
  }, [props.storable.validationState?.validationErrorRecords]);

  if (!isFailed(props.storable.validationState)) {
    return null;
  }

  return (
    <Tooltip text={tooltipContent}>
      <BenchlingIcon className={classNames(css(styles.colorRedDark), props.className)} icon="revision" />
    </Tooltip>
  );
}

export function QueryingStorableValidationStatusIcon(
  props: QueryingStorableValidationStatusIconProps
): JSX.Element | null {
  const queryResult = useBenchlingQuery<
    StorableValidationStatusQuery,
    StorableValidationStatusQueryVariables
  >(STORABLE_VALIDATION_STATUS_QUERY, {
    variables: {storableId: props.storableId},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'cache-and-network',
  });

  if (queryResult.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const storable = queryResult.data.storable;
  if (isInaccessible(storable)) {
    return null;
  }
  return <StorableValidationStatusIcon storable={storable} className={props.className} />;
}

const styles = StyleSheet.create({
  colorRedDark: {
    color: Colors.RED_DARK,
  },
  tooltipText: {
    maxWidth: '200px',
    whiteSpace: 'pre-wrap',
  },
});
